import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import {
  calculateTotalAllBrands,
  calculateTotalHP,
  calculateTotalPercentage,
} from 'legacy/lut/features/partner360/shareOfBusiness'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchPartnerDevicesInfo } from '../api/fetchPartnerDevicesInfo'
import { savePartnerDevicesInfo } from '../api/savePartnerDevicesInfo'
import { applyDraftToResource, createDraftFromData, getHpBrandsByYears } from '../lib/adapters'
import { getDraftPercent, normalizeDraftForCompare, parseDraftNumber } from '../lib/formatters'

import type {
  PartnerDevicesInfoDraft,
  PartnerDevicesInfoFeatureProps,
  PartnerDevicesInfoOverLastYears,
  PartnerDevicesInfoResource,
} from './types'

const isValidNonNegativeNumber = (value: string) => {
  const parsed = parseDraftNumber(value)

  return parsed !== null && Number(parsed) >= 0
}

type ManualHpBrandValues = Pick<PartnerDevicesInfoDraft, 'a3HpBrand' | 'a4HpBrand'>

export const usePartnerDevicesInfo = ({
  partnerId,
  fYear,
}: Pick<PartnerDevicesInfoFeatureProps, 'partnerId' | 'fYear'>) => {
  const [resource, setResource] = useState<PartnerDevicesInfoResource | null>(null)
  const [draft, setDraft] = useState<PartnerDevicesInfoDraft | null>(null)
  const [baselineDraft, setBaselineDraft] = useState<PartnerDevicesInfoDraft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isA3ContractualUnitsAdded, setIsA3ContractualUnitsAdded] = useState(false)
  const [isA4ContractualUnitsAdded, setIsA4ContractualUnitsAdded] = useState(false)
  const manualHpBrandValuesRef = useRef<ManualHpBrandValues | null>(null)

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setSaveError(null)

    try {
      const nextResource = await fetchPartnerDevicesInfo({
        partnerId,
        fYear,
      })
      const nextDraft = createDraftFromData(nextResource.data)

      setResource(nextResource)
      setDraft(nextDraft)
      setBaselineDraft(nextDraft)
      manualHpBrandValuesRef.current = {
        a3HpBrand: nextDraft.a3HpBrand,
        a4HpBrand: nextDraft.a4HpBrand,
      }
      setIsA3ContractualUnitsAdded(false)
      setIsA4ContractualUnitsAdded(false)
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to load partner devices info.')
      setResource(null)
      setDraft(null)
      setBaselineDraft(null)
      manualHpBrandValuesRef.current = null
    } finally {
      setIsLoading(false)
    }
  }, [fYear, partnerId])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const updateDraft = useCallback(
    (updater: (current: PartnerDevicesInfoDraft) => PartnerDevicesInfoDraft) => {
      setDraft((current) => {
        if (!current) {
          return current
        }

        return updater(current)
      })
    },
    []
  )

  const handleFieldChange = useCallback(
    (field: keyof PartnerDevicesInfoDraft, value: string | number | null) => {
      updateDraft((current) => {
        const nextDraft = {
          ...current,
          [field]: value,
        }

        if (field === 'a3HpBrand' || field === 'a4HpBrand') {
          nextDraft.overLastYears = null
          manualHpBrandValuesRef.current = {
            a3HpBrand: nextDraft.a3HpBrand,
            a4HpBrand: nextDraft.a4HpBrand,
          }
        }

        return nextDraft
      })
    },
    [updateDraft]
  )

  const handleOverLastYearsSelect = useCallback(
    (value: number | null) => {
      if (!resource) {
        return
      }

      setSaveError(null)

      updateDraft((current) => {
        if (value === null) {
          const manualHpBrandValues = manualHpBrandValuesRef.current

          return {
            ...current,
            overLastYears: null,
            a3HpBrand: manualHpBrandValues?.a3HpBrand ?? current.a3HpBrand,
            a4HpBrand: manualHpBrandValues?.a4HpBrand ?? current.a4HpBrand,
          }
        }

        if (current.overLastYears === null) {
          manualHpBrandValuesRef.current = {
            a3HpBrand: current.a3HpBrand,
            a4HpBrand: current.a4HpBrand,
          }
        }

        const hpBrands = getHpBrandsByYears(resource.data, value as PartnerDevicesInfoOverLastYears)

        return {
          ...current,
          overLastYears: value,
          a3HpBrand: String(hpBrands.a3),
          a4HpBrand: String(hpBrands.a4),
        }
      })
    },
    [resource, updateDraft]
  )

  const handleReset = useCallback(() => {
    if (!resource) {
      return
    }

    const nextDraft = createDraftFromData(resource.data)
    setDraft(nextDraft)
    setSaveError(null)
    manualHpBrandValuesRef.current = {
      a3HpBrand: nextDraft.a3HpBrand,
      a4HpBrand: nextDraft.a4HpBrand,
    }
    setIsA3ContractualUnitsAdded(false)
    setIsA4ContractualUnitsAdded(false)
  }, [resource])

  const handleAddContractualUnits = useCallback(
    (format: 'a3' | 'a4') => {
      if (!resource) {
        return
      }

      if (format === 'a3' && isA3ContractualUnitsAdded) {
        return
      }

      if (format === 'a4' && isA4ContractualUnitsAdded) {
        return
      }

      const additionalUnits =
        format === 'a3' ? resource.data.phyContractualUnitsA3 : resource.data.phyContractualUnitsA4

      setDraft((current) => {
        if (!current) {
          return current
        }

        const hpKey = format === 'a3' ? 'a3HpBrand' : 'a4HpBrand'
        const allBrandsKey = format === 'a3' ? 'a3AllBrands' : 'a4AllBrands'
        const nextAllBrands = (parseDraftNumber(current[allBrandsKey]) ?? 0) + additionalUnits
        const nextHpBrand = (parseDraftNumber(current[hpKey]) ?? 0) + additionalUnits

        return {
          ...current,
          [allBrandsKey]: String(nextAllBrands),
          [hpKey]: String(nextHpBrand),
        }
      })

      if (format === 'a3') {
        setIsA3ContractualUnitsAdded(true)
      } else {
        setIsA4ContractualUnitsAdded(true)
      }
    },
    [isA3ContractualUnitsAdded, isA4ContractualUnitsAdded, resource]
  )

  const parsedValues = useMemo(() => {
    if (!draft) {
      return null
    }

    const a3AllBrands = parseDraftNumber(draft.a3AllBrands)
    const a3HpBrand = parseDraftNumber(draft.a3HpBrand)
    const a4AllBrands = parseDraftNumber(draft.a4AllBrands)
    const a4HpBrand = parseDraftNumber(draft.a4HpBrand)

    return {
      a3AllBrands,
      a3HpBrand,
      a4AllBrands,
      a4HpBrand,
      totalAllBrands:
        a3AllBrands !== null && a4AllBrands !== null
          ? calculateTotalAllBrands({
              a3AllBrands,
              a4AllBrands,
            })
          : null,
      totalHpBrand:
        a3HpBrand !== null && a4HpBrand !== null
          ? calculateTotalHP({
              a3HP: a3HpBrand,
              a4HP: a4HpBrand,
            })
          : null,
      totalPercentage:
        a3AllBrands !== null && a3HpBrand !== null && a4AllBrands !== null && a4HpBrand !== null
          ? calculateTotalPercentage({
              a3AllBrands,
              a3HP: a3HpBrand,
              a4AllBrands,
              a4HP: a4HpBrand,
            })
          : null,
    }
  }, [draft])

  const percentValues = useMemo(() => {
    if (!draft) {
      return null
    }

    return {
      a3: getDraftPercent(draft.a3HpBrand, draft.a3AllBrands),
      a4: getDraftPercent(draft.a4HpBrand, draft.a4AllBrands),
      total:
        parsedValues?.totalPercentage === null || parsedValues?.totalPercentage === undefined
          ? '-'
          : ValidationHelper.getPercentValue(String(parsedValues.totalPercentage)),
    }
  }, [draft, parsedValues?.totalPercentage])

  const isDirty = useMemo(() => {
    if (!draft || !baselineDraft) {
      return false
    }

    return (
      JSON.stringify(normalizeDraftForCompare(draft)) !==
      JSON.stringify(normalizeDraftForCompare(baselineDraft))
    )
  }, [baselineDraft, draft])

  const validation = useMemo(() => {
    if (!draft || !parsedValues) {
      return null
    }

    const a3PercentValid =
      parsedValues.a3HpBrand === null ||
      parsedValues.a3AllBrands === null ||
      (parsedValues.a3AllBrands === 0
        ? parsedValues.a3HpBrand === 0
        : parsedValues.a3HpBrand <= parsedValues.a3AllBrands)
    const a4PercentValid =
      parsedValues.a4HpBrand === null ||
      parsedValues.a4AllBrands === null ||
      (parsedValues.a4AllBrands === 0
        ? parsedValues.a4HpBrand === 0
        : parsedValues.a4HpBrand <= parsedValues.a4AllBrands)
    const totalPercentValid =
      parsedValues.totalPercentage === null || Number(parsedValues.totalPercentage) <= 100

    return {
      a3AllBrands: isValidNonNegativeNumber(draft.a3AllBrands),
      a3HpBrand: isValidNonNegativeNumber(draft.a3HpBrand),
      a4AllBrands: isValidNonNegativeNumber(draft.a4AllBrands),
      a4HpBrand: isValidNonNegativeNumber(draft.a4HpBrand),
      a3PercentValid,
      a4PercentValid,
      totalPercentValid,
      canSave:
        isValidNonNegativeNumber(draft.a3AllBrands) &&
        isValidNonNegativeNumber(draft.a3HpBrand) &&
        isValidNonNegativeNumber(draft.a4AllBrands) &&
        isValidNonNegativeNumber(draft.a4HpBrand) &&
        a3PercentValid &&
        a4PercentValid &&
        totalPercentValid,
    }
  }, [draft, parsedValues])

  const handleSave = useCallback(async () => {
    if (!resource || !draft || !validation?.canSave || isSaving) {
      return false
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      await savePartnerDevicesInfo({
        partnerId,
        fYear,
        draft,
      })
      const nextResource = applyDraftToResource(resource, draft)
      const nextDraft = createDraftFromData(nextResource.data)
      setResource(nextResource)
      setDraft(nextDraft)
      setBaselineDraft(nextDraft)
      manualHpBrandValuesRef.current = {
        a3HpBrand: nextDraft.a3HpBrand,
        a4HpBrand: nextDraft.a4HpBrand,
      }
      setIsA3ContractualUnitsAdded(false)
      setIsA4ContractualUnitsAdded(false)
      return true
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save partner devices info.')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [draft, fYear, isSaving, partnerId, resource, validation?.canSave])

  return {
    resource,
    draft,
    overLastYearsValue: draft?.overLastYears ?? null,
    isLoading,
    isSaving,
    isA3ContractualUnitsAdded,
    isA4ContractualUnitsAdded,
    loadError,
    saveError,
    isDirty,
    parsedValues,
    percentValues,
    validation,
    loadData,
    handleFieldChange,
    handleOverLastYearsSelect,
    handleReset,
    handleSave,
    handleAddA3ContractualUnits: () => handleAddContractualUnits('a3'),
    handleAddA4ContractualUnits: () => handleAddContractualUnits('a4'),
  }
}
